import type { FC } from 'react'

import { useEffect } from 'react'
import { useRouter } from 'next/router'

const TONE_BACKGROUNDS: Record<string, string> = {
    night: '#1d0838',
    paper: '#f4eee0',
    gold: '#e4d596',
    lilac: '#c6b9e2',
    blue: '#3263ee',
    purple: '#400d72',
    ink: '#141415'
}

// Watches every [data-tone] element and hands the one crossing the middle of the
// viewport to <html data-tone>, which drives the page colours in globals.css.
// The smallest crossing element wins, so a short sentinel inside a section can override it.
export const ToneController: FC = () => {
    const { asPath } = useRouter()

    useEffect(() => {
        const root = document.documentElement
        const elements = Array.from(document.body.querySelectorAll<HTMLElement>('[data-tone]'))
        const crossing = new Set<HTMLElement>()
        const themeColor = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')

        const apply = () => {
            let active: HTMLElement | null = null

            crossing.forEach((element) => {
                if (!active || element.offsetHeight < active.offsetHeight) active = element
            })

            const tone = (active as HTMLElement | null)?.dataset.tone

            if (!tone || root.dataset.tone === tone) return

            root.dataset.tone = tone

            if (themeColor && TONE_BACKGROUNDS[tone]) themeColor.content = TONE_BACKGROUNDS[tone]
        }

        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                const element = entry.target as HTMLElement

                if (entry.isIntersecting) crossing.add(element)
                else crossing.delete(element)
            })

            apply()
        }, { rootMargin: '-49.5% 0px -49.5% 0px' })

        elements.forEach((element) => observer.observe(element))

        return () => observer.disconnect()
    }, [asPath])

    return null
}
